import { supabase } from "../../config/supabaseClient";
import { isCreatorPagesMode } from "../componentsTable";
import { storeUserLinks } from "../SupabaseServices/UserLinksDatabase";
import { uploadImageFile } from "../uploadImage/UploadImage";
import {
  cropImageToFile,
  deriveCoverAlignedCropStateFromImageUrl,
  getContainScale,
  getCropAreaFromViewport,
  LINK_GRID_CROP,
  LINK_LINE_CROP,
  loadImage,
  type CropViewportState,
  type ImageCropPreset,
} from "./cropImageFile";
import {
  getLinkLayoutCropEntry,
  getLinkLayoutDisplayUrl,
  isCropViewportCompatible,
  isFlatPersistedImageCrop,
  mergeLinkLayoutImageCrop,
  resolveLinkLayoutCropState,
  viewportFromPreset,
  type LinkCropLayout,
  type LinkImageCropByLayout,
} from "./cropStatePersist";
import {
  createLayoutSwitchImage,
  getKeyedLayoutDisplayUrl,
  oppositeLinkLayout,
} from "./expandCropAreaToLayout";

export type EnsureLinkRow = {
  id: any;
  link_name?: string | null;
  link_url?: string | null;
  image_url?: string | null;
  original_image_url?: string | null;
  image_crop?: unknown;
};

export type EnsureLinkLayoutPatch = {
  image_url: string;
  image_crop: LinkImageCropByLayout | null;
};

function layoutPreset(layout: LinkCropLayout): ImageCropPreset {
  return layout === "grid" ? LINK_GRID_CROP : LINK_LINE_CROP;
}

export function getLinkLayoutViewport(layout: LinkCropLayout) {
  return viewportFromPreset(layoutPreset(layout));
}

/** Prefer the saved display URL for `layout` (keyed crop → original → active). */
export function withLayoutDisplayImage<T extends EnsureLinkRow>(
  link: T,
  layout: LinkCropLayout
): T {
  const display = getLinkLayoutDisplayUrl(
    link.image_crop,
    layout,
    link.image_url,
    link.original_image_url
  );
  return { ...link, image_url: display ?? link.image_url ?? null };
}

function linkSourceUrl(link: EnsureLinkRow): string {
  if (
    typeof link.original_image_url === "string" &&
    link.original_image_url.trim()
  ) {
    return link.original_image_url.trim();
  }
  if (typeof link.image_url === "string" && link.image_url.trim()) {
    return link.image_url.trim();
  }
  return "";
}

/** True when this layout has no reusable crop and we must generate a new image. */
export function linkNeedsDefaultLayoutCropCreation(
  link: EnsureLinkRow,
  layout: LinkCropLayout
): boolean {
  if (link?.id == null || link.id === 0) return false;
  // Legacy rows may only have image_url (no original / image_crop).
  if (!linkSourceUrl(link)) return false;

  const viewport = getLinkLayoutViewport(layout);
  const cropState = resolveLinkLayoutCropState(
    link.image_crop,
    layout,
    viewport
  );
  const layoutImageUrl = getKeyedLayoutDisplayUrl(link.image_crop, layout);
  if (cropState && layoutImageUrl) return false;

  const entry = getLinkLayoutCropEntry(link.image_crop, layout);
  if (
    cropState &&
    isFlatPersistedImageCrop(link.image_crop) &&
    (entry?.cropViewport.width === 0 ||
      isCropViewportCompatible(entry?.cropViewport, viewport))
  ) {
    return false;
  }

  return true;
}

/**
 * For legacy links (image_url only, no original / layout JSON): capture the
 * layout we're leaving — same cover cropState the cropper uses when opening
 * old images — and store current image_url under that layout key so toggle-back
 * restores the prior preview.
 */
async function snapshotSourceLayoutIfNeeded(args: {
  link: EnsureLinkRow;
  sourceLayout: LinkCropLayout;
  user_id: any;
  username: string;
  collectionName: string;
  from: any;
  componentId?: string | null;
}): Promise<{
  imageCrop: LinkImageCropByLayout | unknown;
  currentImageUrl: string;
}> {
  const { link, sourceLayout } = args;
  const currentImageUrl =
    (typeof link.image_url === "string" && link.image_url.trim()) || "";
  if (!currentImageUrl) {
    return { imageCrop: link.image_crop, currentImageUrl: "" };
  }

  const sourceViewport = getLinkLayoutViewport(sourceLayout);
  const existingUrl = getKeyedLayoutDisplayUrl(link.image_crop, sourceLayout);
  const existingState = resolveLinkLayoutCropState(
    link.image_crop,
    sourceLayout,
    sourceViewport
  );

  // Already have a keyed entry for the layout we're leaving.
  if (existingUrl && existingState) {
    return { imageCrop: link.image_crop, currentImageUrl };
  }

  // Flat crop that matches the source layout — lift under the key with current URL.
  let sourceCropState = existingState;
  if (
    !sourceCropState &&
    isFlatPersistedImageCrop(link.image_crop)
  ) {
    const flatEntry = getLinkLayoutCropEntry(link.image_crop, sourceLayout);
    if (
      flatEntry &&
      (flatEntry.cropViewport.width === 0 ||
        isCropViewportCompatible(flatEntry.cropViewport, sourceViewport))
    ) {
      sourceCropState = flatEntry.cropState;
    }
  }

  // Same as opening a legacy image in the cropper: cover-fit from current preview.
  if (!sourceCropState) {
    sourceCropState = await deriveCoverAlignedCropStateFromImageUrl(
      currentImageUrl,
      sourceViewport.width,
      sourceViewport.height
    );
  }

  const imageCrop = mergeLinkLayoutImageCrop(
    link.image_crop,
    sourceLayout,
    {
      cropState: sourceCropState,
      cropViewport: {
        width: sourceViewport.width,
        height: sourceViewport.height,
      },
      image_url: existingUrl || currentImageUrl,
    },
    sourceViewport
  );

  // Persist source snapshot first (keep active image_url as-is).
  await storeUserLinks(
    link.id,
    args.user_id,
    args.username,
    link.link_name,
    link.link_url,
    currentImageUrl,
    args.collectionName,
    args.from,
    args.componentId,
    undefined,
    imageCrop
  );

  return { imageCrop, currentImageUrl };
}

async function resolveImageSrcForCanvas(sourceUrl: string): Promise<{
  imageSrc: string;
  revoke?: () => void;
}> {
  try {
    const response = await fetch(sourceUrl, { mode: "cors" });
    if (!response.ok) {
      throw new Error("Failed to fetch image");
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return {
      imageSrc: objectUrl,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  } catch {
    return { imageSrc: sourceUrl };
  }
}

async function exportCroppedLinkFile(args: {
  sourceUrl: string;
  cropState: CropViewportState;
  viewport: { width: number; height: number };
  preset: ImageCropPreset;
  fileName: string;
}): Promise<File> {
  const { imageSrc, revoke } = await resolveImageSrcForCanvas(args.sourceUrl);
  try {
    const image = await loadImage(imageSrc);
    const containScale = getContainScale(
      image.naturalWidth,
      image.naturalHeight,
      args.viewport.width,
      args.viewport.height
    );
    const scale = containScale * args.cropState.zoom;
    const cropArea = getCropAreaFromViewport(
      args.cropState.position,
      scale,
      args.viewport.width,
      args.viewport.height
    );
    return await cropImageToFile(
      imageSrc,
      cropArea,
      args.preset.outputWidth,
      args.preset.outputHeight,
      args.fileName
    );
  } finally {
    revoke?.();
  }
}

async function fetchActiveUserLinks(args: {
  user_id: any;
  collectionName: string;
  from: any;
  componentId?: string | null;
}): Promise<EnsureLinkRow[]> {
  const { user_id, collectionName, from, componentId } = args;

  if (isCreatorPagesMode(from) && componentId) {
    const { data, error } = await supabase
      .from("user_links")
      .select(
        "id, link_name, link_url, image_url, original_image_url, image_crop"
      )
      .eq("parent_id", componentId)
      .eq("user_id", user_id)
      .eq("status", "active")
      .order("index");

    if (error) throw error;
    if (data?.length) return data;

    const { data: legacy, error: legacyError } = await supabase
      .from("user_links")
      .select(
        "id, link_name, link_url, image_url, original_image_url, image_crop"
      )
      .eq("collection_name", collectionName)
      .eq("user_id", user_id)
      .eq("type", from)
      .is("parent_id", null)
      .eq("status", "active")
      .order("index");

    if (legacyError) throw legacyError;
    return legacy || [];
  }

  const { data, error } = await supabase
    .from("user_links")
    .select(
      "id, link_name, link_url, image_url, original_image_url, image_crop"
    )
    .eq("collection_name", collectionName)
    .eq("user_id", user_id)
    .eq("type", from)
    .eq("status", "active")
    .order("index");

  if (error) throw error;
  return data || [];
}

async function mapPool<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  if (!items.length) return;
  const queue = [...items];
  const size = Math.min(Math.max(1, concurrency), queue.length);
  await Promise.all(
    Array.from({ length: size }, async () => {
      while (queue.length) {
        const item = queue.shift();
        if (!item) return;
        await worker(item);
      }
    })
  );
}

async function ensureOneLinkLayoutCrop(args: {
  link: EnsureLinkRow;
  layout: LinkCropLayout;
  user_id: any;
  username: string;
  collectionName: string;
  from: any;
  componentId?: string | null;
  onCreating?: (id: any) => void;
}): Promise<EnsureLinkLayoutPatch | null> {
  const {
    link,
    layout,
    user_id,
    username,
    collectionName,
    from,
    componentId,
    onCreating,
  } = args;
  const preset = layoutPreset(layout);
  const viewport = viewportFromPreset(preset);
  const sourceUrl = linkSourceUrl(link);

  if (!sourceUrl && !link.image_url) return null;

  // Only reuse THIS layout's saved crop — never the other layout's cropState.
  const cropState = resolveLinkLayoutCropState(
    link.image_crop,
    layout,
    viewport
  );
  const entry = getLinkLayoutCropEntry(link.image_crop, layout);
  const layoutImageUrl =
    getKeyedLayoutDisplayUrl(link.image_crop, layout) ||
    (typeof entry?.image_url === "string" && entry.image_url.trim()
      ? entry.image_url.trim()
      : null);

  // Keyed layout already has a cropped display URL — activate it.
  if (cropState && layoutImageUrl) {
    if (layoutImageUrl !== link.image_url) {
      await storeUserLinks(
        link.id,
        user_id,
        username,
        link.link_name,
        link.link_url,
        layoutImageUrl,
        collectionName,
        from,
        componentId
      );
    }
    return {
      image_url: layoutImageUrl,
      image_crop: (link.image_crop as LinkImageCropByLayout) || null,
    };
  }

  // Legacy flat crop matches this layout — lift under the key, reuse image.
  if (
    cropState &&
    isFlatPersistedImageCrop(link.image_crop) &&
    (entry?.cropViewport.width === 0 ||
      isCropViewportCompatible(entry?.cropViewport, viewport))
  ) {
    const activeUrl = link.image_url || sourceUrl;
    if (!activeUrl) return null;
    const persisted = mergeLinkLayoutImageCrop(
      link.image_crop,
      layout,
      {
        cropState,
        cropViewport:
          entry && entry.cropViewport.width > 0
            ? entry.cropViewport
            : viewport,
        image_url: activeUrl,
      },
      viewport
    );
    await storeUserLinks(
      link.id,
      user_id,
      username,
      link.link_name,
      link.link_url,
      activeUrl,
      collectionName,
      from,
      componentId,
      link.original_image_url || undefined,
      persisted
    );
    return { image_url: activeUrl, image_crop: persisted };
  }

  if (!sourceUrl) return null;

  onCreating?.(link.id);

  const sourceLayout = oppositeLinkLayout(layout);
  // Legacy (image_url only): freeze current preview under the layout we're
  // leaving before generating the new layout — so toggle-back is unchanged.
  const { imageCrop: cropAfterSourceSnapshot, currentImageUrl } =
    await snapshotSourceLayoutIfNeeded({
      link,
      sourceLayout,
      user_id,
      username,
      collectionName,
      from,
      componentId,
    });

  const originalUrl =
    typeof link.original_image_url === "string" &&
    link.original_image_url.trim()
      ? link.original_image_url.trim()
      : "";
  const previousDisplayUrl =
    getKeyedLayoutDisplayUrl(cropAfterSourceSnapshot, sourceLayout) ||
    currentImageUrl ||
    null;
  // Prefer true original. Never treat the other layout's display crop as the
  // "original" for createLayoutSwitchImage — cropState coords are for the
  // full bitmap and produce garbage framing on an already-cropped file.
  const exportSource = originalUrl || currentImageUrl || sourceUrl;
  const fileName = `link-${layout}-${link.id || "new"}-${Date.now()}.png`;

  let stateToUse: CropViewportState | null = cropState;
  let file: File | null = null;
  let workingCrop: unknown = cropAfterSourceSnapshot;

  // Expand prior framing into the new aspect only when we have a real original.
  if (!stateToUse && originalUrl) {
    try {
      const { imageSrc, revoke } = await resolveImageSrcForCanvas(originalUrl);
      try {
        const created = await createLayoutSwitchImage({
          originalImageSrc: imageSrc,
          imageCrop: workingCrop,
          targetLayout: layout,
          targetViewport: viewport,
          sourceViewport: getLinkLayoutViewport(sourceLayout),
          outputWidth: preset.outputWidth,
          outputHeight: preset.outputHeight,
          fileName,
          previousDisplayUrl,
        });
        if (created) {
          // Always the freshly calculated TARGET layout cropState (grid≠line).
          stateToUse = created.cropState;
          file = created.file;
        }
      } finally {
        revoke?.();
      }
    } catch (error) {
      console.warn(
        `Could not cover-fit prior crop for link ${link.id}; using cover fit.`,
        error
      );
    }
  }

  // No original (or expand failed): cover-fit the image the user was already
  // seeing so grid→line / line→grid stays visually continuous.
  const coverSource = previousDisplayUrl || exportSource;
  if (!stateToUse) {
    stateToUse = await deriveCoverAlignedCropStateFromImageUrl(
      coverSource,
      viewport.width,
      viewport.height
    );
  }
  if (!file) {
    file = await exportCroppedLinkFile({
      sourceUrl: coverSource,
      cropState: stateToUse,
      viewport,
      preset,
      fileName,
    });
  }

  const uploadedUrl = await uploadImageFile({
    file,
    username,
    storageName: "mylinks",
  });
  // Write target layout; keep source layout entry from the snapshot above.
  const persisted = mergeLinkLayoutImageCrop(
    workingCrop,
    layout,
    {
      cropState: stateToUse,
      cropViewport: {
        width: viewport.width,
        height: viewport.height,
      },
      image_url: uploadedUrl,
    },
    viewport
  );
  await storeUserLinks(
    link.id,
    user_id,
    username,
    link.link_name,
    link.link_url,
    uploadedUrl,
    collectionName,
    from,
    componentId,
    // Do not invent original_image_url from display for legacy rows.
    originalUrl || undefined,
    persisted
  );
  return { image_url: uploadedUrl, image_crop: persisted };
}

/**
 * For each link: use the saved crop for `layout` when present; otherwise
 * create a cover-aligned default from the original and persist it under that
 * layout key. Also syncs active `image_url` to the layout display URL.
 */
export async function ensureUserLinksHaveLayoutCrop(args: {
  user_id: any;
  username: string;
  collectionName: string;
  from: any;
  componentId?: string | null;
  layout: LinkCropLayout;
  /** When provided, skip re-fetching links. */
  links?: EnsureLinkRow[];
  onCreating?: (id: any) => void;
  onSettled?: (id: any, patch: EnsureLinkLayoutPatch | null) => void;
}): Promise<void> {
  const {
    user_id,
    username,
    collectionName,
    from,
    componentId,
    layout,
    onCreating,
    onSettled,
  } = args;

  const links =
    args.links ||
    (await fetchActiveUserLinks({
      user_id,
      collectionName,
      from,
      componentId,
    }));

  await mapPool(links, 3, async (link) => {
    if (link?.id == null || link.id === 0) {
      onSettled?.(link?.id, null);
      return;
    }
    try {
      const patch = await ensureOneLinkLayoutCrop({
        link,
        layout,
        user_id,
        username,
        collectionName,
        from,
        componentId,
        onCreating,
      });
      onSettled?.(link.id, patch);
    } catch (error) {
      console.error(
        `Failed to ensure ${layout} crop for link ${link.id}:`,
        error
      );
      onSettled?.(link.id, null);
    }
  });
}
